"use client";

import { useState } from "react";
import Link from "next/link";
import type { CatalogAbility } from "@bash/shared";
import { integrationLogoUrl } from "@/lib/abilities/integration-logo";
import { firstGlyph, isIconUrl } from "@/lib/abilities/icon";
import { prefetchAbility } from "@/lib/abilities/prefetch";
import { VestraLogoMark } from "@/components/ui/VestraLogoMark";

/** Cards a section shows before the "Show N more" expander takes over. The
 * skeleton draws the same number, so the first paint is the real shape. */
export const SECTION_CAP = 6;

export function formatInstalls(n: number): string {
  if (n >= 10000) return `${Math.round(n / 1000)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return n.toLocaleString();
}

/** Molten gild gradient for our own stroked icons. userSpaceOnUse: the default
 * objectBoundingBox units collapse to nothing on pure h/v line paths. */
export function AbilityGildDefs() {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden>
      <defs>
        <linearGradient
          id="ab-gild"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="24"
          y2="24"
        >
          <stop offset="0" stopColor="var(--gold-molten-hi)" />
          <stop offset="0.55" stopColor="var(--gold-molten-mid)" />
          <stop offset="1" stopColor="var(--gold-molten-lo)" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export interface WorksCoinSrc {
  src: string;
  /** Monochrome-dark glyph (GitHub, Notion) — inverted in dark theme. */
  inv?: boolean;
  /** Integration name for the row's accessible label. Slug-derived rows fill
   * this in; authored `srcs` may leave it off and stay decorative. */
  name?: string;
}

const MAX_COINS = 4;

/** Overlapping "works with" brand-logo coins. Real rows pass `slugs`
 * (CDN-resolved, self-healing via onError); hardcoded banner slides pass
 * local `srcs`. */
export function WorksCoins({
  slugs,
  srcs,
  size = "md",
}: {
  slugs?: string[];
  srcs?: WorksCoinSrc[];
  size?: "md" | "lg";
}) {
  const [failed, setFailed] = useState(() => new Set<string>());
  // The catalog returns slugs in no stable order — sort so the same ability
  // shows the same coins on every render. `srcs` keep their authored order.
  const coins: WorksCoinSrc[] =
    srcs ??
    [...(slugs ?? [])]
      .sort()
      .map((s) => ({ src: integrationLogoUrl(s), name: s }));
  const visible = coins.filter((c) => !failed.has(c.src)).slice(0, MAX_COINS);
  if (visible.length === 0) return null;
  // The coins are decorative images, so the row itself carries the meaning for
  // screen readers — named after the coins actually on screen (404s and the
  // overflow past MAX_COINS are already gone).
  const names = visible.map((c) => c.name).filter((n): n is string => !!n);
  return (
    <span
      className={
        size === "lg"
          ? "ab-works ab-works--lg flex items-center shrink-0 [&_img]:w-6 [&_img]:h-6 [&_img]:rounded-full [&_img]:bg-bg-elevated [&_img]:border border-border-subtle [&_img]:p-1.5 [&_img]:-ml-2 [&_img]:shadow-cards [&_img:first-child]:ml-0 [&_img]:w-7 [&_img]:h-7 [&_img]:p-1.5"
          : "ab-works flex items-center shrink-0 [&_img]:w-6 [&_img]:h-6 [&_img]:rounded-full [&_img]:bg-bg-elevated [&_img]:border border-border-subtle [&_img]:p-1.5 [&_img]:-ml-2 [&_img]:shadow-cards [&_img:first-child]:ml-0"
      }
      role={names.length > 0 ? "img" : undefined}
      aria-label={
        names.length > 0 ? `Works with ${names.join(", ")}` : undefined
      }
    >
      {visible.map((c) => (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={c.src}
          src={c.src}
          alt=""
          role="presentation"
          className={c.inv ? "inv" : undefined}
          onError={() => setFailed((prev) => new Set(prev).add(c.src))}
        />
      ))}
    </span>
  );
}

/** "6 skills · 2 agents · 2 MCPs" — zero parts drop out, ones singularize.
 * Exported for the card's own test. */
export function invLine(counts: CatalogAbility["counts"]): string {
  const parts: Array<[number, string]> = [
    [counts.skills, "skill"],
    [counts.agents, "agent"],
    [counts.mcps, "MCP"],
  ];
  return parts
    .filter(([n]) => n > 0)
    .map(([n, word]) => `${n} ${word}${n === 1 ? "" : "s"}`)
    .join(" · ");
}

function ByLine({ ability }: { ability: CatalogAbility }) {
  return (
    <span className="inline-flex items-center gap-1 text-3 text-fg-subtle whitespace-nowrap overflow-hidden text-ellipsis min-w-0 [&_.ab-by-mark]:w-3.5 [&_.ab-by-mark]:h-3.5 [&_.ab-by-mark]:rounded-4 [&_.ab-by-mark]:shrink-0 [&_b]:text-fg-default [&_b]:font-medium">
      by
      {/* The author's own logo always wins; the bundled Vestra mark covers
 vestra-official rows minted before manifests carried a logoUrl.
 alt="" everywhere — the author name follows in text, so a named
 mark makes a screen reader say "Vestra Vestra". */}
      {ability.author.logoUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={ability.author.logoUrl}
          alt=""
          aria-hidden
          className="ab-by-mark"
        />
      ) : ability.author.id === "vestra-official" ? (
        <VestraLogoMark size={13} alt="" className="ab-by-mark" />
      ) : null}
      <b>{ability.author.name}</b>
      {ability.installed_count > 0 &&
        ` · ${formatInstalls(ability.installed_count)} installs`}
    </span>
  );
}

/** The living foot: a lit dot plus what this ability is doing for you.
 * My Kit is a system ability but it's the user's own, so it says so. */
function StateLine({ ability }: { ability: CatalogAbility }) {
  const label =
    ability.state === "system"
      ? "always on"
      : ability.state === "personal"
        ? "yours"
        : "installed";
  const dim =
    ability.state === "system"
      ? ability.slug === "my-kit"
        ? "· yours"
        : "· by Vestra"
      : ability.state === "installed" && ability.installed_version
        ? `· v${ability.installed_version}`
        : null;
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-3 tracking-[0.04em] text-gold-ink [font-feature-settings:'tnum'] whitespace-nowrap min-w-0 min-h-7 [&_.dim]:text-fg-subtle [&_.dim]:overflow-hidden [&_.dim]:text-ellipsis [&_.live]:w-1.5 [&_.live]:h-1.5 [&_.live]:rounded-full [&_.live]:bg-cards-gradient [&_.live]:shadow-cards-2 [&_.live]:shrink-0">
      <span className="live" />
      {label}
      {dim && <span className="dim">{dim}</span>}
    </span>
  );
}

/** Structure-true placeholder: the real banner plate and six real card boxes
 * with their contents blanked, so nothing shifts when the catalog lands.
 *
 * Lives here rather than in AbilitiesTab so the route-level skeleton can
 * compose it without pulling the whole tab — modal, API client and all —
 * into the bundle that has to paint first.
 */
export function TabSkeleton() {
  return (
    <div
      className="ab-scope [--ab-gold-line:color-mix(in_srgb,_var(--gold-molten-mid)_30%,_transparent)] [--ab-gold-wash:color-mix(in_srgb,_var(--gold-molten-mid)_7%,_transparent)] [--ab-plate-shadow:0_1px_2px_rgba(40,_30,_10,_0.05),_0_22px_48px_-30px_rgba(50,_38,_16,_0.25)] [--ab-card-shadow:0_1px_2px_rgba(40,_30,_10,_0.04),_0_14px_32px_-24px_rgba(50,_38,_16,_0.22)] [--ab-amber:#b45309]"
      role="status"
      aria-label="Loading abilities"
    >
      <div className="relative mb-11.5" aria-hidden>
        {/* 320 / 35 are the real banner plate and section head measured at
 1440px — the skeleton lands the grid at the same y. */}
        <div className="ab-banner active [grid-area:1_/_1] relative overflow-hidden bg-bg-elevated border border-border-default rounded-24 shadow-ab-plate-shadow grid grid-cols-[minmax(0,_1fr)_auto] gap-8 items-center pt-6.5 md:pt-8.5 pb-10 px-5 sm:px-6.5 md:pl-9.5 md:pr-11 opacity-0 pointer-events-none transition-opacity duration-[450ms] [&.active]:opacity-100 [&.active]:pointer-events-auto [&.active]:z-[1] before:[content:''] before:absolute before:inset-0 before:bg-ab-gold-side before:pointer-events-none after:[content:''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-0.5 after:bg-gold-molten-sheen after:opacity-75 max-md:grid-cols-[1fr] motion-reduce:transition-none h-[320px]">
          <div className="relative min-w-0 flex flex-col gap-3">
            <span className="ab-skel-block bg-bg-muted rounded-8 w-[96px] h-[18px]" />
            <span className="ab-skel-block bg-bg-muted rounded-8 w-[58%] h-[30px]" />
            <span className="ab-skel-block bg-bg-muted rounded-8 w-[84%] h-[12px]" />
            <span className="ab-skel-block bg-bg-muted rounded-8 w-[62%] h-[12px]" />
            <span className="ab-skel-block bg-bg-muted rounded-8 w-[132px] h-[34px]" />
          </div>
          {/* 330×210 = the first real slide's hero mark, so the body column
 doesn't widen when the catalog lands. */}
          <span className="ab-skel-block max-md:hidden bg-bg-muted rounded-20 w-[330px] h-[210px]" />
        </div>
      </div>
      {/* A ghost section head too — real content puts one above every grid,
 and without it everything shifts down when the cards arrive. */}
      <section className="ab-sect" aria-hidden>
        <div className="ab-sect__head flex items-baseline gap-3 mb-4.5">
          <span className="ab-skel-block bg-bg-muted rounded-8 w-[180px] h-[35px]" />
          <span className="flex-1 h-px bg-border-subtle transform-[translateY(-6px)]" />
        </div>
        <div className="grid grid-cols-[repeat(auto-fill,_minmax(300px,_1fr))] gap-3 sm:gap-4">
          {Array.from({ length: SECTION_CAP }, (_, i) => (
            <div
              key={i}
              className="ab-card ab-card--ghost relative overflow-hidden bg-bg-elevated border border-border-subtle rounded-20 pt-5 px-5 pb-4 flex flex-col gap-2.5 shadow-ab-card-shadow text-[inherit] no-underline transition-[border-color,transform,box-shadow] duration-220 ease-out after:[content:''] after:absolute after:inset-0 after:bg-ab-gold-corner after:opacity-56 after:pointer-events-none [&:hover_.ab-btn-install]:border-ab-gold-line [&:hover_.ab-btn-install]:text-gold-ink [&:hover_.ab-btn-install]:bg-ab-gold-wash hover:[&_.ab-btn-install]:opacity-100 hover:[&_.ab-btn-install]:transform-[translateY(0)] can-hover:[&:focus-within_.ab-btn-install]:opacity-100 can-hover:[&:focus-within_.ab-btn-install]:transform-[translateY(0)] hover:[&_.ab-btn-install]:transform-[translateY(0)_scale(0.98)] can-hover:[&:focus-within_.ab-btn-install]:transform-[translateY(0)_scale(0.98)] cursor-default transform-[none] hover:cursor-default hover:transform-[none] hover:border-border-subtle hover:shadow-ab-card-shadow"
            >
              <div className="flex items-center gap-3 min-w-0 [&_.ab-works]:ml-auto">
                <span className="ab-skel-block bg-bg-muted rounded-12 w-[40px] h-[40px]" />
                <span className="ab-skel-block bg-bg-muted rounded-8 w-[48%] h-[13px]" />
              </div>
              <span className="ab-skel-block bg-bg-muted rounded-8 w-[90%] h-[11px]" />
              <span className="ab-skel-block bg-bg-muted rounded-8 w-[75%] h-[11px]" />
              <div className="flex items-center justify-between gap-2.5 mt-auto pt-3 border-t border-border-subtle [&_>_.ab-btn-install:only-child]:ml-auto">
                <span className="ab-skel-block bg-bg-muted rounded-8 w-[40%] h-[11px]" />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/**
 * One cream-gold card for every shelf. The body is identical everywhere;
 * only the foot changes — an author byline plus an Install affordance for
 * catalog rows, a living-state line for the abilities already working.
 */
export function AbilityCard({
  ability,
  installing,
  onInstall,
}: {
  ability: CatalogAbility;
  installing: boolean;
  onInstall?: (a: CatalogAbility) => void;
}) {
  const comingSoon = !!ability.comingSoon;
  const iconAsUrl = isIconUrl(ability.icon);
  const own = !iconAsUrl && ability.author.id === "vestra-official";
  const inv = invLine(ability.counts);
  const living = !comingSoon && ability.state !== "available";

  const body = (
    <>
      <div className="flex items-center gap-3 min-w-0 [&_.ab-works]:ml-auto">
        <span
          className={
            own
              ? "w-10 h-10 rounded-12 bg-bg-base border border-border-subtle flex items-center justify-center shrink-0 text-5 leading-none [&_img]:w-5.5 [&_img]:h-5.5 [&_img]:block bg-gold-molten-wash border-gold-line-subtle-20"
              : "w-10 h-10 rounded-12 bg-bg-base border border-border-subtle flex items-center justify-center shrink-0 text-5 leading-none [&_img]:w-5.5 [&_img]:h-5.5 [&_img]:block"
          }
          aria-hidden
        >
          {iconAsUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={ability.icon!} alt="" className="ab-chip-ico__photo" />
          ) : (
            ability.icon || firstGlyph(ability.name)
          )}
        </span>
        <span className="ab-card__name text-4 font-semibold tracking-[-0.01em] text-fg-default min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
          {ability.name}
        </span>
        <WorksCoins slugs={ability.integrations} />
      </div>
      <p className="text-3.5 text-fg-muted leading-[1.55] line-clamp-2">
        {ability.description}
      </p>
      {inv && (
        <div className="ab-card__inv font-mono text-3 text-gold-ink [font-feature-settings:'tnum'] leading-normal">
          {inv}
        </div>
      )}
      <div className="flex items-center justify-between gap-2.5 mt-auto pt-3 border-t border-border-subtle [&_>_.ab-btn-install:only-child]:ml-auto">
        {living ? (
          <>
            <StateLine ability={ability} />
            {ability.update_available && (
              <span className="inline-flex items-center font-mono text-2.5 font-medium tracking-[0.04em] py-1 px-2 rounded-100 shrink-0 text-ab-amber bg-ab-amber/9 border border-solid border-ab-amber/22">
                update ready
              </span>
            )}
          </>
        ) : (
          <>
            <ByLine ability={ability} />
            {comingSoon ? (
              <button
                type="button"
                className="ab-btn-install [font-family:inherit] text-3.5 font-medium text-fg-muted bg-none border border-border-default rounded-10 py-2 px-4 cursor-pointer shrink-0 transition-colors duration-200 can-hover:opacity-0 can-hover:transform-[translateY(2px)] can-hover:transition-app-card"
              >
                Notify me
              </button>
            ) : onInstall ? (
              <button
                type="button"
                className="ab-btn-install [font-family:inherit] text-3.5 font-medium text-fg-muted bg-none border border-border-default rounded-10 py-2 px-4 cursor-pointer shrink-0 transition-colors duration-200 can-hover:opacity-0 can-hover:transform-[translateY(2px)] can-hover:transition-app-card"
                onClick={(e) => {
                  // The card is the link — without these the click navigates
                  // to the detail page instead of opening the install modal.
                  e.preventDefault();
                  e.stopPropagation();
                  onInstall(ability);
                }}
              >
                {installing ? "Checking..." : "Install"}
              </button>
            ) : null}
          </>
        )}
      </div>
    </>
  );

  // Coming-soon rows have no detail page, so they render as a plain div —
  // no navigation, no prefetch.
  return comingSoon ? (
    <div className="ab-card relative overflow-hidden bg-bg-elevated border border-border-subtle rounded-20 pt-5 px-5 pb-4 flex flex-col gap-2.5 cursor-pointer shadow-ab-card-shadow text-[inherit] no-underline transition-[border-color,transform,box-shadow] duration-220 ease-out after:[content:''] after:absolute after:inset-0 after:bg-ab-gold-corner after:opacity-56 after:pointer-events-none hover:border-ab-gold-line hover:transform-[translateY(-2px)] hover:shadow-app-card [&:hover_.ab-btn-install]:border-ab-gold-line [&:hover_.ab-btn-install]:text-gold-ink [&:hover_.ab-btn-install]:bg-ab-gold-wash hover:[&_.ab-btn-install]:opacity-100 hover:[&_.ab-btn-install]:transform-[translateY(0)] can-hover:[&:focus-within_.ab-btn-install]:opacity-100 can-hover:[&:focus-within_.ab-btn-install]:transform-[translateY(0)] hover:[&_.ab-btn-install]:transform-[translateY(0)_scale(0.98)] can-hover:[&:focus-within_.ab-btn-install]:transform-[translateY(0)_scale(0.98)]">
      {body}
    </div>
  ) : (
    <Link
      href={`/abilities/${ability.slug}`}
      className="ab-card relative overflow-hidden bg-bg-elevated border border-border-subtle rounded-20 pt-5 px-5 pb-4 flex flex-col gap-2.5 cursor-pointer shadow-ab-card-shadow text-[inherit] no-underline transition-[border-color,transform,box-shadow] duration-220 ease-out after:[content:''] after:absolute after:inset-0 after:bg-ab-gold-corner after:opacity-56 after:pointer-events-none hover:border-ab-gold-line hover:transform-[translateY(-2px)] hover:shadow-app-card [&:hover_.ab-btn-install]:border-ab-gold-line [&:hover_.ab-btn-install]:text-gold-ink [&:hover_.ab-btn-install]:bg-ab-gold-wash hover:[&_.ab-btn-install]:opacity-100 hover:[&_.ab-btn-install]:transform-[translateY(0)] can-hover:[&:focus-within_.ab-btn-install]:opacity-100 can-hover:[&:focus-within_.ab-btn-install]:transform-[translateY(0)] hover:[&_.ab-btn-install]:transform-[translateY(0)_scale(0.98)] can-hover:[&:focus-within_.ab-btn-install]:transform-[translateY(0)_scale(0.98)]"
      onMouseEnter={() => prefetchAbility(ability.slug)}
      onFocus={() => prefetchAbility(ability.slug)}
    >
      {body}
    </Link>
  );
}
